import { Stack, Button, Typography, Alert, useTheme, useMediaQuery } from '@mui/material';
import { useState, useEffect, useRef } from 'react';

import { useNavigate, useLocation } from 'react-router-dom';
import PlansSection from './components/Plans/Plans';
import {
  useCreateCheckoutSession,
  fetchUpdateUser,
  useUpdatePaymentMethod,
  useDowngradeAtPeriodEnd,
} from '@/services/noteGoApi/noteGoApiComponents';
import { useAuthStore } from '@/stores/authStore';
import { AccountStatusTypes } from '@/helpers/accountStatus';
import { RouteMap } from '@/routes/Routes';
import ActiveSubscriptionDetails from './components/ActiveSubscriptionDetails/ActiveSubscriptionDetails';
import PlanUsageCard from './components/PlanUsageCard/PlanUsageCard';
import { usePricingData } from '@/hooks/usePricingData';
import useAlertStore from '@/stores/alertStore';
import CountdownBanner from '@/components/CountdownBanner';
import { getFreePlanProductId, isFreePlan, initializePricingCache } from '@/services/subscriptionService';
import { errorReporter } from '@/services/errorReportingService';
import { rememberCheckoutPrice } from '@/utils/checkoutPrice';

const PlanAndPayments = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { mutateAsync: createCheckoutSession, isPending: isCreatingCheckout } = useCreateCheckoutSession();
  const { mutate: updatePaymentMethod } = useUpdatePaymentMethod();
  const { mutate: downgradeSubscription } = useDowngradeAtPeriodEnd();

  const { userToken, accountStatus, userData, fetchAndSetUserData, loading: authLoading } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  // Hook call kept (no destructure) to warm the pricing cache for child components
  usePricingData();
  const showAlert = useAlertStore((state) => state.showAlert);
  const [isActivating, setIsActivating] = useState(false);
  const [isUpdatingPaymentInfo, setIsUpdatingPaymentInfo] = useState(false);
  const [freePlanProductId, setFreePlanProductId] = useState<string | null>(null);
  const [isUserOnFreePlan, setIsUserOnFreePlan] = useState(false);
  const navigateTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Clear pending navigation timer on unmount so the user isn't yanked back to home
  useEffect(() => {
    return () => {
      if (navigateTimeoutRef.current) clearTimeout(navigateTimeoutRef.current);
    };
  }, []);

  // Scroll to anchor (plans section) when navigating with hash
  useEffect(() => {
    const hash = location.hash.slice(1); // Remove the '#' prefix
    if (!hash) return;

    // Retry scroll a few times to handle late-rendered content (e.g. async pricingData)
    const timeoutIds: number[] = [];

    const attemptScroll = (retriesLeft: number) => {
      const element = document.getElementById(hash);
      if (element) {
        // Respect user's reduced motion preference for accessibility
        const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
        const behavior: ScrollBehavior = prefersReducedMotion ? 'auto' : 'smooth';
        element.scrollIntoView({ behavior, block: 'start' });
      }

      // Keep re-asserting the scroll across the retry budget: cards above the
      // anchor (PlanUsageCard, ActiveSubscriptionDetails) hydrate async and grow
      // after the first scroll, pushing the target down. Don't bail on first hit.
      if (retriesLeft > 0) {
        const timeoutId = window.setTimeout(() => {
          attemptScroll(retriesLeft - 1);
        }, 100);
        timeoutIds.push(timeoutId);
      }
    };

    // Use requestAnimationFrame to ensure initial paint, then start retries
    const rafId = requestAnimationFrame(() => attemptScroll(10));

    return () => {
      cancelAnimationFrame(rafId);
      timeoutIds.forEach((id) => clearTimeout(id));
    };
  }, [location.hash]);

  // Initialize pricing cache and get free plan info
  useEffect(() => {
    const initPricing = async () => {
      try {
        await initializePricingCache();
        const freeId = await getFreePlanProductId();
        setFreePlanProductId(freeId);

        if (userData?.subscription?.product_id) {
          const userOnFree = await isFreePlan(userData.subscription.product_id);
          setIsUserOnFreePlan(userOnFree);
        }
      } catch (err) {
        errorReporter.critical('Failed to initialize pricing data', err, {
          feature: 'subscription',
          operation: 'initPricing',
          productId: userData?.subscription?.product_id,
        });
        showAlert('Unable to load pricing information. Please refresh the page or contact support.', 'error');
      }
    };
    initPricing();
  }, [userData?.subscription?.product_id]);

  // Ensure user data is fetched when component mounts
  useEffect(() => {
    if (userToken && !userData && !authLoading) {
      fetchAndSetUserData();
    }
  }, [userToken, userData, authLoading, fetchAndSetUserData]);

  // Auto-activate free plan for users without subscription
  useEffect(() => {
    const autoActivateFreePlan = async () => {
      if (userData && !userData.subscription && !isActivating) {
        await handleActivateFreePlan();
      }
    };
    autoActivateFreePlan().catch((err) => {
      errorReporter.critical('Auto-activate free plan failed unexpectedly', err, {
        feature: 'subscription',
        operation: 'autoActivateFreePlan',
        userId: userData?.id,
      });
    });
  }, [userData]); // Only depend on userData to avoid infinite loops

  const handleCheckout = (priceId: string, _subscriptionId?: string | null, stripeCustomerId?: string | null) => {
    if (!userToken) {
      showAlert('Please log in again to continue.', 'error');
      return;
    }

    // A second session opened while the first is still in flight bills the user
    // twice for the same upgrade — Stripe treats each completed session as an
    // independent subscription.
    if (isCreatingCheckout) return;

    // Always route through Stripe checkout so users can enter or update their card
    const userEmail = userData?.email || null;

    createCheckoutSession({
      body: {
        price_id: priceId,
        customer_id: stripeCustomerId,
        customer_email: userEmail,
      },
      headers: { authorization: `Bearer ${userToken}` },
    })
      .then((response) => {
        rememberCheckoutPrice(response.session_id, priceId);
        navigate(`${RouteMap.CHECKOUT}/${response.client_secret}`);
      })
      .catch((err) => {
        // Check for network errors (backend not running)
        const isNetworkError = err?.message?.includes('Network error') || err?.name === 'unknown';
        if (isNetworkError) {
          errorReporter.expected('Checkout failed - network error', err, {
            feature: 'stripe',
            operation: 'createCheckoutSession',
            priceId,
          });
          showAlert(
            'Unable to connect to server. Please try again later or contact support if the problem persists.',
            'error',
          );
        } else {
          errorReporter.critical('Failed to create checkout session', err, {
            feature: 'stripe',
            operation: 'createCheckoutSession',
            priceId,
          });
          showAlert('Failed to create checkout session. Please try again.', 'error');
        }
      });
  };

  const handleUpdatePaymentInfo = () => {
    if (!userData?.subscription?.stripe_customer_id) {
      showAlert('No payment account found. Please select a plan below to set up your subscription.', 'error');
      return;
    }

    const stripeCustomerId = userData.subscription.stripe_customer_id;

    setIsUpdatingPaymentInfo(true);
    updatePaymentMethod(
      {
        body: {
          customer_id: stripeCustomerId,
        },
        headers: { authorization: `Bearer ${userToken}` },
      },
      {
        onSuccess: (response) => {
          window.location.href = response.url;
          setIsUpdatingPaymentInfo(false);
        },
        onError: (err) => {
          errorReporter.critical('Failed to create payment method update session', err, {
            feature: 'stripe',
            operation: 'updatePaymentMethod',
            customerId: stripeCustomerId,
          });
          showAlert('Failed to update payment information. Please try again or contact support.', 'error');
          setIsUpdatingPaymentInfo(false);
        },
      },
    );
  };

  const handleUpgrade = (priceId: string, subscriptionId?: string | null, stripeCustomerId?: string | null) => {
    handleCheckout(priceId, subscriptionId, stripeCustomerId);
  };

  // Handle downgrade to free plan when user selects FREE plan from the plans grid.
  // `selectedProductId` is the product_id of the plan the user actually clicked;
  // fall back to the resolved free plan id for the generic entry points.
  const handleDowngradeToFree = (selectedProductId?: string) => {
    if (!userToken) {
      showAlert('Please log in again to continue.', 'error');
      return;
    }

    // Confirm downgrade
    if (
      !window.confirm(
        'Are you sure you want to downgrade to the Free plan? This takes effect immediately and your paid features will end now.',
      )
    ) {
      return;
    }

    const targetProductId = selectedProductId || freePlanProductId;
    if (!targetProductId) {
      showAlert('Unable to determine free plan. Please refresh the page and try again.', 'error');
      return;
    }

    downgradeSubscription(
      {
        body: { product_id: targetProductId },
        headers: { authorization: `Bearer ${userToken}` },
      },
      {
        onSuccess: () => {
          showAlert('Your subscription has been downgraded to the Free plan.', 'success');
          fetchAndSetUserData({ force: true });
        },
        onError: (err) => {
          errorReporter.critical('Failed to downgrade subscription to free', err, {
            feature: 'stripe',
            operation: 'downgradeAtPeriodEnd',
          });
          showAlert('Failed to downgrade subscription. Please try again.', 'error');
        },
      },
    );
  };

  const handleActivateFreePlan = async () => {
    if (!userData || !userToken) {
      errorReporter.expected('Activate free plan blocked - missing userData or userToken', null, {
        feature: 'subscription',
        operation: 'activateFreePlan',
        hasUserData: !!userData,
        hasUserToken: !!userToken,
      });
      showAlert('Unable to activate plan. Please refresh the page and try again.', 'error');
      return;
    }

    if (!freePlanProductId) {
      errorReporter.expected('Activate free plan blocked - free plan product ID not loaded', null, {
        feature: 'subscription',
        operation: 'activateFreePlan',
      });
      showAlert('Unable to determine free plan. Please refresh the page and try again.', 'error');
      return;
    }

    setIsActivating(true);

    try {
      await fetchUpdateUser({
        headers: { authorization: `Bearer ${userToken}` },
        pathParams: { userId: userData.id },
        body: {
          subscription: {
            id: userData.subscription?.id,
            product_id: freePlanProductId,
            price_id: null,
            stripe_customer_id: null,
            status: 'active',
            card_last_four_digits: null,
          },
        },
      });

      await fetchAndSetUserData({ force: true });
      showAlert('Free plan activated! Redirecting...', 'success');

      // Redirect to home after a short delay
      navigateTimeoutRef.current = setTimeout(() => {
        navigate(RouteMap.HOME);
      }, 1500);
    } catch (error) {
      errorReporter.critical('Failed to activate free plan', error, {
        feature: 'subscription',
        operation: 'activateFreePlan',
        userId: userData.id,
        freePlanProductId,
      });
      showAlert('Failed to activate free plan. Please try again or contact support.', 'error');
    } finally {
      setIsActivating(false);
    }
  };

  // Show emergency button for users stuck without any plan or with incorrect status
  // Note: Free plan users now have status 'active' like all other plans
  const isStuckWithoutPlan =
    userData && (!userData.subscription || (isUserOnFreePlan && !userData.subscription?.status));

  // Show loading state while auth data is being loaded
  if (authLoading || !userToken) {
    return (
      <Stack
        p={isMobile ? 2 : 4}
        alignItems={'center'}
        width={'100%'}
        sx={{
          boxSizing: 'border-box',
          overflow: 'hidden',
          maxWidth: '100vw',
        }}
      >
        <Typography>Loading...</Typography>
      </Stack>
    );
  }

  return (
    <Stack
      p={isMobile ? 2 : 4}
      alignItems={'center'}
      width={'100%'}
      sx={{
        boxSizing: 'border-box',
        overflow: 'hidden',
        maxWidth: '100vw',
      }}
    >
      {isStuckWithoutPlan && (
        <Alert
          severity="warning"
          sx={{
            mb: 3,
            width: isMobile ? '100%' : '90%',
            '& .MuiAlert-action': {
              alignItems: isMobile ? 'flex-start' : 'center',
              pl: isMobile ? 0 : 3,
              pt: isMobile ? 2 : 0,
            },
          }}
          action={
            <Button
              variant="contained"
              color="primary"
              size="medium"
              onClick={handleActivateFreePlan}
              disabled={isActivating}
              sx={{
                whiteSpace: isMobile ? 'normal' : 'nowrap',
                fontWeight: 600,
                width: isMobile ? '100%' : 'auto',
              }}
            >
              {isActivating ? 'Activating...' : 'Activate Free Plan'}
            </Button>
          }
        >
          <Typography variant="body1">
            Your account doesn't have a plan assigned. Click the button to activate your free plan and continue using
            the app.
          </Typography>
        </Alert>
      )}
      {userData?.subscription?.scheduled_to_end && userData?.subscription?.status !== 'active' && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body1">Your subscription is scheduled to end on.</Typography>
        </Alert>
      )}
      {userData?.subscription?.status === 'incomplete' && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="body1">
            Your subscription setup is not yet complete. Please update your payment details to finish activating your
            plan.
          </Typography>
          <br />
          <Button
            variant="contained"
            color="primary"
            onClick={handleUpdatePaymentInfo}
            disabled={isUpdatingPaymentInfo}
          >
            {isUpdatingPaymentInfo ? 'Updating...' : 'Update Payment Info'}
          </Button>
        </Alert>
      )}
      {(userData?.subscription?.status === 'payment_failed' || userData?.subscription?.status === 'past_due') && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <Typography variant="body1">
            Your subscription payment was unsuccessful. You have a 30-day grace period to update your payment details
            and avoid service interruption.
          </Typography>
          <br />
          {userData?.subscription?.current_period_end && (
            <CountdownBanner text="Subscription ends in:" endDate={userData.subscription.current_period_end} />
          )}
          <br />
          <Button
            variant="contained"
            color="primary"
            onClick={handleUpdatePaymentInfo}
            disabled={isUpdatingPaymentInfo}
          >
            {isUpdatingPaymentInfo ? 'Updating...' : 'Update Payment Info'}
          </Button>
        </Alert>
      )}
      {accountStatus === AccountStatusTypes.ACTIVE && !isUserOnFreePlan && <ActiveSubscriptionDetails />}
      {accountStatus === AccountStatusTypes.ACTIVE && <PlanUsageCard />}
      <PlansSection
        handleUpgrade={handleUpgrade}
        onDowngradeToFree={handleDowngradeToFree}
        currentUserPlan={userData?.subscription}
      />
    </Stack>
  );
};

export default PlanAndPayments;
